import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import styles from "./ColorInputModal.module.css";
import { v4 } from "uuid";
import Color from "color";

import CustomColorInput from "../CustomColorInput";
import ColorRadioOption from "../ColorRadioOption";
import convertVariableColor from "NewsAdmin/utils/convertVariableColor";

/**
 * Interactive component. A modal that allows for a user to select a color for the text or background of a feed item.
 */
export function ColorInputModal({
  customColor,
  setCustomColor,
  currentColor,
  setCurrentColor,
}) {
  // CSS variables can be used with the function convertVariableColor()
  // They have been replaced with their hex values for efficiency
  const defaultOptions = [
    { color: "#ffffff", name: "color-a" },
    { color: "#000000", name: "color-b" },
    { color: "#243746", name: "strength" },
    { color: "#415364", name: "empowerment" },
    { color: "#91b6bb", name: "inspiration" },
    { color: "#7F6A45", name: "stability" },
    { color: "#9E703C", name: "authenticity" },
    { color: "#ADB7C0", name: "balance" },
    { color: "#A89C96", name: "connection" },
  ];

  const id = v4();

  // Validate color
  const defaultRef = useRef();
  const customRef = useRef();

  const [tempCustomColor, setTempCustomColor] = useState(customColor);

  // Determines if the custom should be clicked after a new custom color is inputted.
  // Uses a state value to ensure that the ColorRadioOption color is changed before the click event.
  const [customClick, setCustomClick] = useState(false);
  useEffect(() => {
    if (customClick) {
      setCustomClick(false);
      customRef.current.firstChild.click();
    }
  }, [customClick]);

  const handleTempColor = (value) => {
    setTempCustomColor(value);
    // If empty, default to "default"
    if (value === "") {
      defaultRef.current.firstChild.click();
    } else {
      let error = false;
      try {
        // Converts to color value if stored as a root variable
        const convertedValue = convertVariableColor(value) ?? value;
        Color(convertedValue);
      } catch {
        error = true;
      }
      // Defaults to black if invalid
      const newCustom = !error ? value : "#000";
      setCustomColor(newCustom);
      setCustomClick(true);
    }
  };

  // Split the currentColor value.
  const currentColorPair = currentColor.split(" ");

  return (
    <div className={styles.container}>
      <fieldset className={styles.fieldset} name={`color-fieldset-${id}`}>
        <legend hidden>Color</legend>
        <div className={styles.row}>
          <div ref={defaultRef}>
            <ColorRadioOption
              key="default"
              color="#FFF"
              name="default"
              isSelected={currentColorPair[0] === "default"}
              setCurrentColor={setCurrentColor}
            />
          </div>
          {defaultOptions.map((option) => (
            <ColorRadioOption
              {...option}
              isSelected={currentColorPair[0] === option.name}
              setCurrentColor={setCurrentColor}
              key={option.name}
            />
          ))}
        </div>
        <div ref={customRef}>
          <ColorRadioOption
            key="custom"
            setCurrentColor={setCurrentColor}
            color={convertVariableColor(customColor) ?? customColor}
            name="custom"
            isSelected={currentColorPair[0] === "custom"}
          />
        </div>
      </fieldset>
      <CustomColorInput
        customColor={tempCustomColor}
        setCustomColor={(value) => handleTempColor(value)}
      />
    </div>
  );
}

ColorInputModal.propTypes = {
  /**
   * The value of the custom color.
   */
  customColor: PropTypes.string,
  /**
   * Function that sets the value of customColor.
   */
  setCustomColor: PropTypes.func,
  /**
   * The value of the currently selected color.
   * Should be in the format of `{name} {color}`.
   */
  currentColor: PropTypes.string,
  /**
   * Function that sets the currentColor (selected color).
   */
  setCurrentColor: PropTypes.func,
};
